import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, Settings, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

interface CookiePreferences {
  essential: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

export const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always true, não pode ser desabilitado
    functional: true,
    analytics: true,
    marketing: false
  });
  const navigate = useNavigate();

  useEffect(() => {
    // Verifica se o usuário já aceitou os cookies
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Mostra o banner após 2 segundos
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setIsVisible(false);
    
    // Aqui você pode inicializar os cookies/scripts aceitos
    initializeCookies(allAccepted);
  };

  const handleAcceptSelected = () => {
    const selectedPreferences = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('cookieConsent', JSON.stringify(selectedPreferences));
    setIsVisible(false);
    
    // Inicializa apenas os cookies aceitos
    initializeCookies(selectedPreferences);
  };

  const handleReject = () => {
    const onlyEssential = {
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('cookieConsent', JSON.stringify(onlyEssential));
    setIsVisible(false);
    
    // Inicializa apenas cookies essenciais
    initializeCookies(onlyEssential);
  };

  const initializeCookies = (prefs: any) => {
    // Aqui você pode inicializar os diferentes tipos de cookies/scripts
    if (prefs.analytics) {
      // Inicializar Google Analytics, etc.
      console.log('Analytics cookies enabled');
    }
    
    if (prefs.marketing) {
      // Inicializar pixels de marketing, etc.
      console.log('Marketing cookies enabled');
    }
    
    if (prefs.functional) {
      // Inicializar cookies funcionais
      console.log('Functional cookies enabled');
    }
  };

  const updatePreference = (type: keyof CookiePreferences) => {
    if (type === 'essential') return; // Não pode ser desabilitado
    
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const cookieTypes = [
    {
      id: 'essential' as keyof CookiePreferences,
      title: 'Cookies Essenciais',
      description: 'Necessários para o funcionamento básico da plataforma. Não podem ser desabilitados.',
      required: true
    },
    {
      id: 'functional' as keyof CookiePreferences,
      title: 'Cookies Funcionais',
      description: 'Melhoram sua experiência, como lembrança de preferências e configurações.',
      required: false
    },
    {
      id: 'analytics' as keyof CookiePreferences,
      title: 'Cookies Analíticos',
      description: 'Nos ajudam a entender como você usa a plataforma para melhorá-la continuamente.',
      required: false
    },
    {
      id: 'marketing' as keyof CookiePreferences,
      title: 'Cookies de Marketing',
      description: 'Permitem personalizar anúncios e conteúdo baseado em seus interesses.',
      required: false
    }
  ];

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-4"
      >
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Cookie className="h-6 w-6 text-[#00B6F0]" />
              <h3 className="text-xl font-bold text-white">Cookies & Privacidade</h3>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsVisible(false)}
              className="text-gray-400 hover:text-white"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Main Content */}
          {!showDetails ? (
            <div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Utilizamos cookies para melhorar sua experiência na Arena Pro Player. 
                Alguns são essenciais para o funcionamento, enquanto outros nos ajudam 
                a personalizar conteúdo e analisar nosso tráfego.
              </p>

              {/* Quick Actions */}
              <div className="space-y-3">
                <Button
                  onClick={handleAcceptAll}
                  className="w-full bg-gradient-to-r from-[#00B6F0] to-[#0077B6] text-black font-bold hover:opacity-90"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Aceitar Todos
                </Button>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowDetails(true)}
                    className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Personalizar
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={handleReject}
                    className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
                  >
                    Apenas Essenciais
                  </Button>
                </div>
              </div>

              {/* Links */}
              <div className="mt-4 pt-4 border-t border-gray-700">
                <p className="text-sm text-gray-400">
                  Saiba mais em nossa{" "}
                  <button
                    onClick={() => navigate("/politica-privacidade")}
                    className="text-[#00B6F0] hover:underline"
                  >
                    Política de Privacidade
                  </button>
                </p>
              </div>
            </div>
          ) : (
            <div>
              {/* Detailed Settings */}
              <p className="text-gray-300 mb-4">
                Escolha quais tipos de cookies você deseja aceitar:
              </p>

              <div className="space-y-4 mb-6">
                {cookieTypes.map((cookie) => (
                  <div key={cookie.id} className="bg-gray-800/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-white">{cookie.title}</h4>
                      <button
                        onClick={() => updatePreference(cookie.id)}
                        disabled={cookie.required}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          preferences[cookie.id]
                            ? 'bg-[#00B6F0]'
                            : 'bg-gray-600'
                        } ${cookie.required ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            preferences[cookie.id] ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    <p className="text-sm text-gray-400">{cookie.description}</p>
                    {cookie.required && (
                      <p className="text-xs text-[#00B6F0] mt-1">Obrigatório</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button
                  onClick={handleAcceptSelected}
                  className="w-full bg-gradient-to-r from-[#00B6F0] to-[#0077B6] text-black font-bold"
                >
                  Salvar Preferências
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => setShowDetails(false)}
                  className="w-full border-gray-600 text-gray-300 hover:bg-gray-800"
                >
                  Voltar
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}; 